import PropTypes from 'prop-types';
import { useEnhancedQuery } from '../../hooks';
import { hasGraphQLError, isOutOfScopeDuplicate } from '../../utils';
import {
  CUSTOMER,
  OPPORTUNITY,
} from '../../graphql';
import {
  clientCustomer,
  clientOpportunity,
} from '../..';

/**
 * Custom hook to check if the user has access to a customer
 * @param {string} customerId - The customer ID to check access for
 * @param {boolean} shouldShowAll - Sales Admin view-all
 * @param {boolean} skip - Don't fetch; the customer is known to be outside the caller's scope
 * @returns {boolean} - Whether the user has access
 */
function useCustomerAccess(customerId, shouldShowAll, skip) {
  const { error } = useEnhancedQuery(CUSTOMER.READ(), {
    disableDefaultError: true,
    options: {
      skip,
      client: clientCustomer,
      variables: {
        id: customerId,
        all: shouldShowAll,
      },
    },
  });
  if (skip || hasGraphQLError(error, 'forbidden')) {
    return false;
  }
  return true;
}

/**
 * Custom hook to fetch opportunities for a customer
 * @param {string} customerId - The customer ID to fetch opportunities for
 * @param {boolean} shouldShowAll - Whether to show all opportunities
 * @param {boolean} skip - Don't fetch (customer outside the caller's scope)
 * @returns {Array} - Array of opportunities
 */
function useCustomerOpportunities(customerId, shouldShowAll, skip) {
  const { data } = useEnhancedQuery(OPPORTUNITY.LIST(), {
    options: {
      skip,
      client: clientOpportunity,
      variables: {
        customer_id: customerId,
        sorts: [{ field: 'created_at', order: 'dsc' }],
        limit: 50,
        per_query: 50,
        pit_id: '',
        search_after: '',
        // CPI-1197: Grant Vietnam Sales Admin L1-level access
        all: shouldShowAll,
      },
    },
    defaultData: {
      listOpportunities: {
        opportunities: [],
        count: 0,
      },
    },
    selector: (d) => d.listOpportunities,
  });

  return (data && data.opportunities) || [];
}

/**
 * Wrapper component that handles data fetching for a single customer row.
 * This component calls hooks at the top level, complying with the Rules of Hooks.
 */
export default function CustomerRow({ customer, shouldShowAll, renderRow }) {
  // LMS-5183: a masked out-of-scope duplicate would only get forbidden back (and the opportunity
  // list has no disableDefaultError, so it would redirect to /forbidden); don't query it.
  const outOfScope = isOutOfScopeDuplicate(customer);
  const haveAccess = useCustomerAccess(customer.id, shouldShowAll, outOfScope);
  const opportunities = useCustomerOpportunities(customer.id, shouldShowAll, outOfScope);

  const customerWithData = {
    ...(customer.contact || {}),
    id: customer.id,
    opportunities: outOfScope ? [] : opportunities,
    haveAccess,
    outOfScope,
  };

  return renderRow(customerWithData);
}

CustomerRow.propTypes = {
  customer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    contact: PropTypes.object,
  }).isRequired,
  shouldShowAll: PropTypes.bool.isRequired,
  renderRow: PropTypes.func.isRequired,
};
